import { ArrowPathIcon } from '@heroicons/react/24/outline';
import type { ShotsData, ScriptCharacter, ScriptProp, ScriptScene } from '@packages/global';
import { useTranslation } from 'react-i18next';
import ShotsSection from '../sections/ShotsSection';

/**
 * ShotsStep 组件属性
 */
export interface ShotsStepProps {
  /** 步骤数据 */
  data: ShotsData | null;
  /** 脚本 ID */
  scriptId?: string;
  /** 角色列表（用于分镜编辑时选择） */
  characters?: ScriptCharacter[];
  /** 道具列表（用于分镜编辑时选择） */
  props?: ScriptProp[];
  /** 场景列表（用于分镜编辑时选择） */
  scenes?: ScriptScene[];
  /** 是否正在加载 */
  isLoading?: boolean;
  /** 是否正在生成 */
  isGenerating?: boolean;
  /** 生成进度 */
  generatingProgress?: { current: number; total: number; isGenerating: boolean };
  /** 新分镜索引（用于动画） */
  newShotIndices?: number[];
  /** 清除新分镜索引回调 */
  onClearNewShotIndices?: () => void;
  /** 重新生成回调 */
  onRegenerate?: () => void;
  /** 数据更新回调 */
  onUpdate?: () => void;
}

/**
 * 空状态组件
 */
function EmptyState({ onRegenerate }: { onRegenerate?: () => void }) {
  const { t } = useTranslation(['script', 'common']);
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-bg-elevated flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-3.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-1.5A1.125 1.125 0 0118 18.375M20.625 4.5H3.375m17.25 0c.621 0 1.125.504 1.125 1.125M20.625 4.5h-1.5C18.504 4.5 18 5.004 18 5.625m3.75 0v1.5c0 .621-.504 1.125-1.125 1.125M3.375 4.5c-.621 0-1.125.504-1.125 1.125M3.375 4.5h1.5C5.496 4.5 6 5.004 6 5.625m-3.75 0v1.5c0 .621.504 1.125 1.125 1.125m0 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m1.5-3.75C5.496 8.25 6 7.746 6 7.125v-1.5M4.875 8.25C5.496 8.25 6 8.754 6 9.375v1.5m0-5.25v5.25m0-5.25C6 5.004 6.504 4.5 7.125 4.5h9.75c.621 0 1.125.504 1.125 1.125m1.125 2.625h1.5m-1.5 0A1.125 1.125 0 0118 7.125v-1.5m1.125 2.625c-.621 0-1.125.504-1.125 1.125v1.5m2.625-2.625c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125M18 5.625v5.25M7.125 12h9.75m-9.75 0A1.125 1.125 0 016 10.875M7.125 12C6.504 12 6 12.504 6 13.125m0-2.25C6 11.496 5.496 12 4.875 12M18 10.875c0 .621-.504 1.125-1.125 1.125M18 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m-12 5.25v-5.25m0 5.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125m-12 0v-1.5c0-.621-.504-1.125-1.125-1.125M18 18.375v-5.25m0 5.25v-1.5c0-.621.504-1.125 1.125-1.125M18 13.125v1.5c0 .621.504 1.125 1.125 1.125M18 13.125c0-.621.504-1.125 1.125-1.125M6 13.125v1.5c0 .621-.504 1.125-1.125 1.125M6 13.125C6 12.504 5.496 12 4.875 12m-1.5 0h1.5m14.25 0h1.5" />
        </svg>
      </div>
      <h3 className="text-base font-medium text-gray-200 mb-2">{t('script:shots.empty')}</h3>
      <p className="text-sm text-gray-400 mb-6 max-w-xs">
        {t('script:shots.emptyDesc')}
      </p>
      {onRegenerate && (
        <button
          onClick={onRegenerate}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary hover:bg-primary-dark text-white text-sm font-medium transition-colors cursor-pointer"
        >
          <ArrowPathIcon className="w-4 h-4" />
          {t('script:shots.generate')}
        </button>
      )}
    </div>
  );
}

/**
 * ShotsStep 组件
 * 
 * 复用原有的 ShotsSection 组件，支持：
 * - 分镜编辑（画面描述、场景、构图、运镜、配音、台词等）
 * - 分镜删除
 * - 出现元素编辑（角色、道具）
 * 
 * Requirements: 6.8
 */
export default function ShotsStep({
  data,
  scriptId,
  characters = [],
  props = [],
  scenes = [],
  isLoading = false,
  isGenerating = false,
  generatingProgress,
  newShotIndices = [],
  onClearNewShotIndices,
  onRegenerate,
  onUpdate,
}: ShotsStepProps) {
  const { t } = useTranslation(['script', 'common']);

  // 加载状态
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <ArrowPathIcon className="w-8 h-8 text-primary animate-spin" />
          <span className="text-sm text-gray-400">{t('script:shots.loading')}</span>
        </div>
      </div>
    );
  }

  // 生成中状态（无数据时显示生成进度）
  if (isGenerating && (!data || data.shots.length === 0)) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <ArrowPathIcon className="w-8 h-8 text-primary animate-spin" />
          <span className="text-sm text-gray-400">
            {generatingProgress?.total
              ? t('script:shots.generatingProgress', { current: generatingProgress.current, total: generatingProgress.total })
              : t('script:shots.generating')}
          </span>
        </div>
      </div>
    );
  }

  // 空状态
  if (!data || data.shots.length === 0) {
    return <EmptyState onRegenerate={onRegenerate} />;
  }

  return (
    <div className="space-y-4">
      {/* 使用原有的 ShotsSection 组件 */}
      <ShotsSection
        shots={data.shots}
        scriptId={scriptId}
        onUpdate={onUpdate}
        scenes={scenes}
        characters={characters}
        props={props}
        isGenerating={isGenerating}
        generatingProgress={generatingProgress}
        newShotIndices={newShotIndices}
        onClearNewShotIndices={onClearNewShotIndices}
      />
    </div>
  );
}
